import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  VideoCamera,
  Upload,
  SpinnerGap,
  CheckCircle,
  Warning,
  FilePdf,
  User,
  Play,
  Waveform,
  Sparkle,
  Clock,
  ArrowRight,
  Eye,
  DownloadSimple
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

const VOICES = [
  { id: "onyx", name: "Onyx", desc: "Grave, autoritaire" },
  { id: "nova", name: "Nova", desc: "Energique, dynamique" },
  { id: "alloy", name: "Alloy", desc: "Neutre, balance" },
  { id: "echo", name: "Echo", desc: "Calme, posee" },
  { id: "shimmer", name: "Shimmer", desc: "Brillante, joyeuse" },
  { id: "fable", name: "Fable", desc: "Expressive, narrative" },
];

const STAGES = {
  pdf_extraction: { label: "Extraction PDF", pct: 10, eta: "~5s" },
  script_generation: { label: "Génération du script IA", pct: 30, eta: "~15s" },
  tts_generation: { label: "Narration audio TTS", pct: 50, eta: "~20s" },
  video_generation: { label: "Génération vidéo avatar HD", pct: 70, eta: "~1-5 min" },
  knowledge_extraction: { label: "Extraction connaissances", pct: 80, eta: "~30s" },
  finalizing: { label: "Finalisation", pct: 90, eta: "~10s" },
  audio_ready: { label: "Audio et script prets", pct: 100, eta: "" },
  complete: { label: "Terminé", pct: 100, eta: "" },
};

export default function AIVideoPage() {
  const { lang, t } = useLang();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("fr");
  const [voice, setVoice] = useState("onyx");
  const [ceoPhoto, setCeoPhoto] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [currentGen, setCurrentGen] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [selectedGen, setSelectedGen] = useState(null);
  const [error, setError] = useState("");
  const pollingRef = useRef(null);
  const photoInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    loadGenerations();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const loadGenerations = async () => {
    try {
      const { data } = await axios.get(`${API}/api/ai-video/generations`, { withCredentials: true });
      setGenerations(data);
    } catch {}
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCeoPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleGenerate = async () => {
    if (!ceoPhoto || !pdfFile || !title) return;
    setGenerating(true);
    setError("");
    
    const formData = new FormData();
    formData.append("ceo_photo", ceoPhoto);
    formData.append("pdf_file", pdfFile);
    formData.append("title", title);
    formData.append("language", language);
    formData.append("voice", voice);
    
    try {
      const { data } = await axios.post(`${API}/api/ai-video/generate`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setCurrentGen(data.generation_id);
      startPolling(data.generation_id);
    } catch (err) {
      console.error("Génération failed:", err);
      setError(err.response?.data?.detail || err.message || "Erreur lors de la génération");
      setGenerating(false);
    }
  };

  const startPolling = (genId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/ai-video/generations/${genId}`, { withCredentials: true });
        setSelectedGen(data);
        if (data.status === "complete" || data.status === "failed" || data.status === "audio_ready") {
          clearInterval(pollingRef.current);
          setGenerating(false);
          loadGenerations();
        }
      } catch {}
    }, 3000);
  };

  const stageInfo = selectedGen ? (STAGES[selectedGen.stage] || { label: selectedGen.stage, pct: 0 }) : null;

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit'] flex items-center gap-3" data-testid="ai-video-title">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center">
            <Sparkle size={22} weight="fill" className="text-white" />
          </div>
          Générateur Vidéo IA
        </h1>
        <p className="text-muted-foreground mt-1">Photo du CEO + PDF = Video de formation avec avatar IA parlant</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Upload Form ── */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-5">
          <h2 className="font-semibold font-['Outfit']">1. Importez vos fichiers</h2>

          {/* Title */}
          <div className="space-y-2">
            <Label>Titre de la video</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Formation Produits Financiers 2026"
              data-testid="ai-video-title-input"
            />
          </div>

          {/* CEO Photo */}
          <div className="space-y-2">
            <Label>Photo du presentateur (CEO)</Label>
            {photoPreview ? (
              <div className="flex items-center gap-4 p-4 border border-border rounded-xl">
                <img src={photoPreview} alt="CEO" className="w-16 h-16 rounded-full object-cover border-2 border-primary/30" />
                <div>
                  <p className="text-sm font-medium">{ceoPhoto?.name}</p>
                  <button onClick={() => { setCeoPhoto(null); setPhotoPreview(null); }} className="text-xs text-destructive hover:underline mt-1">Supprimer</button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  data-testid="ceo-photo-input"
                />
                <button
                  type="button"
                  className="w-full h-20 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); photoInputRef.current.click(); }}
                  data-testid="ceo-photo-btn"
                >
                  <User size={24} className="text-muted-foreground" />
                  <span className="text-sm font-medium">Choisir une photo</span>
                  <span className="text-[10px] text-muted-foreground">JPG, PNG - Photo de face</span>
                </button>
              </div>
            )}
          </div>

          {/* PDF */}
          <div className="space-y-2">
            <Label>Document PDF du sujet</Label>
            {pdfFile ? (
              <div className="flex items-center gap-4 p-4 border border-border rounded-xl">
                <FilePdf size={28} className="text-red-500" />
                <div>
                  <p className="text-sm font-medium">{pdfFile.name}</p>
                  <button onClick={() => setPdfFile(null)} className="text-xs text-destructive hover:underline mt-1">Supprimer</button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  className="hidden"
                  data-testid="pdf-input"
                />
                <button
                  type="button"
                  className="w-full h-20 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); pdfInputRef.current.click(); }}
                  data-testid="pdf-btn"
                >
                  <FilePdf size={24} className="text-muted-foreground" />
                  <span className="text-sm font-medium">Choisir un PDF</span>
                  <span className="text-[10px] text-muted-foreground">Le contenu sera transforme en script</span>
                </button>
              </div>
            )}
          </div>

          {/* Voice + Language */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Langue</Label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full h-10 px-3 bg-background border border-input rounded-md text-sm"
                data-testid="ai-video-language"
              >
                <option value="fr">Francais</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Voix</Label>
              <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                className="w-full h-10 px-3 bg-background border border-input rounded-md text-sm"
                data-testid="ai-video-voice"
              >
                {VOICES.map((v) => (
                  <option key={v.id} value={v.id}>{`${v.name} - ${v.desc}`}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status indicators */}
          <div className="flex items-center gap-4 text-sm">
            <span className={`flex items-center gap-1.5 ${title ? "text-teal-600" : "text-muted-foreground"}`}>
              {title ? <CheckCircle size={16} weight="fill" /> : <span className="w-4 h-4 rounded-full border-2 border-current" />}
              Titre
            </span>
            <span className={`flex items-center gap-1.5 ${ceoPhoto ? "text-teal-600" : "text-muted-foreground"}`}>
              {ceoPhoto ? <CheckCircle size={16} weight="fill" /> : <span className="w-4 h-4 rounded-full border-2 border-current" />}
              Photo
            </span>
            <span className={`flex items-center gap-1.5 ${pdfFile ? "text-teal-600" : "text-muted-foreground"}`}>
              {pdfFile ? <CheckCircle size={16} weight="fill" /> : <span className="w-4 h-4 rounded-full border-2 border-current" />}
              PDF
            </span>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generating || !ceoPhoto || !pdfFile || !title}
            className="w-full h-12 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white font-semibold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="generate-ai-video-btn"
          >
            {generating ? (
              <><SpinnerGap size={20} className="animate-spin mr-2" /> {t("generating")}</>
            ) : !ceoPhoto || !pdfFile || !title ? (
              <>{!title ? "Entrez un titre" : !ceoPhoto ? "Sélectionnez une photo" : "Sélectionnez un PDF"}</>
            ) : (
              <><Sparkle size={20} weight="fill" className="mr-2" /> Générer la Vidéo IA</>
            )}
          </Button>
        </div>

        {/* ── Status / Result Panel ── */}
        <div className="space-y-5">
          {/* Active Génération Status */}
          {selectedGen && (selectedGen.status === "processing" || selectedGen.status === "audio_ready" || selectedGen.status === "complete") && (
            <div className="bg-card border border-border rounded-xl p-6" data-testid="generation-status">
              <div className="flex items-center gap-3 mb-4">
                {selectedGen.status === "complete" || selectedGen.status === "audio_ready" ? (
                  <CheckCircle size={24} className="text-teal-500" weight="fill" />
                ) : (
                  <SpinnerGap size={24} className="text-primary animate-spin" />
                )}
                <div>
                  <h3 className="font-semibold">{selectedGen.title}</h3>
                  <p className="text-xs text-muted-foreground">{stageInfo?.label}{stageInfo?.eta ? <span className="ml-2 text-primary font-medium">{stageInfo.eta}</span> : null}</p>
                </div>
              </div>

              {/* Progress */}
              <div className="h-2 bg-secondary rounded-full overflow-hidden mb-4">
                <div
                  className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-violet-500 to-blue-500"
                  style={{ width: `${selectedGen.progress || stageInfo?.pct || 0}%` }}
                />
              </div>

              {/* Pipeline stages */}
              <div className="grid grid-cols-6 gap-1 mb-4">
                {Object.entries(STAGES).filter(([k]) => !["audio_ready", "complete"].includes(k)).map(([key, val]) => {
                  const isDone = (stageInfo?.pct || 0) > val.pct;
                  const isActive = selectedGen.stage === key;
                  return (
                    <div key={key} className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full transition-all ${isDone ? "bg-teal-500" : isActive ? "bg-primary animate-pulse" : "bg-secondary"}`} />
                      <span className="text-[9px] text-center mt-1 text-muted-foreground leading-tight">{val.label.split(" ")[0]}</span>
                    </div>
                  );
                })}
              </div>

              {/* Script preview */}
              {selectedGen.script && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Script généré</h4>
                  <div className="bg-secondary/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{selectedGen.script}</p>
                  </div>
                </div>
              )}

              {/* Audio player */}
              {(selectedGen.status === "audio_ready" || selectedGen.status === "complete") && (
                <div className="mt-4 flex items-center gap-3">
                  <audio controls className="flex-1 h-10" data-testid="audio-player">
                    <source src={`${API}/api/ai-video/generations/${selectedGen.id}/audio`} type="audio/mpeg" />
                  </audio>
                  <a
                    href={`${API}/api/ai-video/generations/${selectedGen.id}/audio`}
                    download
                    className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    data-testid="download-audio-btn"
                  >
                    <DownloadSimple size={18} className="text-muted-foreground" />
                  </a>
                </div>
              )}

              {/* Note if no FAL key */}
              {selectedGen.note && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
                  <p className="text-xs text-amber-700 dark:text-amber-300">{selectedGen.note}</p>
                </div>
              )}

              {selectedGen.error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                  <p className="text-xs text-red-700 dark:text-red-300">{selectedGen.error}</p>
                </div>
              )}
            </div>
          )}

          {/* Previous Generations */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <VideoCamera size={18} className="text-primary" />
              <h2 className="font-semibold font-['Outfit']">{t("ai_video_history")}</h2>
            </div>
            <div className="divide-y divide-border">
              {generations.length > 0 ? (
                generations.map((gen) => (
                  <button
                    key={gen.id}
                    onClick={() => setSelectedGen(gen)}
                    className="w-full px-5 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
                    data-testid={`gen-${gen.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      gen.status === "complete" ? "bg-teal-100 dark:bg-teal-500/10" :
                      gen.status === "audio_ready" ? "bg-blue-100 dark:bg-blue-500/10" :
                      gen.status === "failed" ? "bg-red-100 dark:bg-red-500/10" :
                      "bg-secondary"
                    }`}>
                      {gen.status === "complete" ? <CheckCircle size={16} className="text-teal-600" weight="fill" /> :
                       gen.status === "audio_ready" ? <Waveform size={16} className="text-blue-600" /> :
                       gen.status === "failed" ? <Warning size={16} className="text-red-500" /> :
                       <SpinnerGap size={16} className="text-primary animate-spin" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{gen.title}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {gen.status === "audio_ready" ? "Audio pret" : gen.status} - {new Date(gen.created_at).toLocaleString("fr-FR")}
                      </p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Sparkle size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucune vidéo générée encore</p>
                  <p className="text-xs mt-1">Importez une photo et un PDF pour commencer</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
